/**
 * Gestión de un track
 *
 * @link https://www.mendikat.net
 * @author Javi Urrutia <javi@mendikat.net>
 * @copyright 2023-2026 Mendikat
 * @license Mendikat
 *
 */

// Gestión de las modales comerciales
import Business from '../../common/business';

// Gestión de Google Adsense
import Adsense from '../../common/adsense';

// Leaftlet
import { LMap, LTileLayer, LControl, LMarker, LIcon, LPopup} from 'vue2-leaflet';
import LControlFullscreen from 'vue2-leaflet-fullscreen';

import { latLng } from 'leaflet';

// Estilos propios de Lefalet
import 'leaflet/dist/leaflet.css';
L.Icon.Default.prototype.options.imagePath = '/images/vendor/leaflet/dist/';

// Plugins de Leaflet
import './gpx';
import './elevation';

// VueCoolLightbox
// @link https://vue-cool-lightbox.lucaspulliese.com
import CoolLightBox from 'vue-cool-lightbox';
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css';

// Vuejs-Datepicker
// @link https://www.npmjs.com/package/vuejs-datepicker
import Datepicker from 'vuejs-datepicker';
import {ca, en, eu, es, fr, gl, pt} from 'vuejs-datepicker/dist/locale';

// Vue Upload Component
// @author Lian Yue
// @link https://github.com/lian-yue/vue-upload-component
import VueUploadComponent from 'vue-upload-component';

import draggable from 'vuedraggable';

// moment.js
// @link https://momentjs.com/
import moment from 'moment';

// numeral
// @link http://numeraljs.com/
import numeral from 'numeral';

// mobile-device-detect
// @link https://github.com/duskload/mobile-device-detect
import { isMobile } from 'mobile-device-detect';

const { default: Axios } = require('axios');

const vm = new Vue({
    el: '#app',
    components: {
        LMap,
        LTileLayer,
        LControl,
        LMarker,
        LIcon,
        LPopup,
        LControlFullscreen,
        coollightbox : CoolLightBox,
        FileUpload   : VueUploadComponent,
        draggable,
        Datepicker,
        tinymce,
        isMobile,
    },
    mixins: [Business, Adsense],
    data:
        {
            // El idioma actual de la página
            lang : 'es',

            // Idiomas del selector de fechas
            ca, en, eu, es, fr, gl, pt,

            // La cumbre
            mountain : null,

            // El track
            track    :
                {
                    description : null,
                    text        : null,
                },

            // La lista de nuevos archivos de imagen a subir
            files       : [],

            // El modo en que se mostrando la información
            // Puede ser en modo visor o en modo edición para cambiar la información
            // 'view'|'edit'
            mode      : 'view',

            // El método (callback) a ejecutar cuando se selecciona un usuario
            // Un usuario puede ser seleccionado para añadirlo a los autores de la montaña,
            // para cambiar la autoría de una imagen
            userSelect: null,

            // El mapa
            map :
                {
                    object      : null,                                 // EL propio objeto
                    zoom        : 15,                                   // El nivel de zoom
                    center      : latLng(0, 0),    // El centro del mapa
                    provider    : null,                                 // El proveedor de cartografía
                    summits     : [],                                   // Una lista de cumbres representadas
                },

            // Los proveedores de cartografía digital
            providers : [],

            // La lista de imágenes
            images: [],

            // La imagen que muestra el visor actualmente
            image       : null,

            // Información de la imagen actualmente mostrada
            currentImage: null,

            // Búsqueda de usuarios
            searched :
                {
                    user     : null,        // El usuario buscado
                    users    : [],          // Los usuarios resultantes de la búsqueda
                },

            // La lista de comentarios registrados
            posts       : [],

            // Opciones de subida de imágenes
            upload      :
                {
                    progress     : 0,                       // El grado de progreso de subida en tanto por cieto
                    maxFileSize  : 10 * 1024 * 1024,        // EL tamaño máximo de archivo adminido en bytes
                    imagesAcceptedTypes:                    // Los tipos de archivos de imagen aceptados
                        [
                            'image/jpeg',
                        ]
                },

            // La información de la autoría (información sobre el autor seleccionado)
            authorInfo : null,

            // Aprobación de las condiciones de uso
            useConditionsAccepted: false,

            // El usuario actual (si es un usuario registrado)
            user: {},

            // Temporizador para que se cierre automáticamente una alerta
            dismiss:
                {
                    countDown : 0,      // Contador (tiempo) actual de la alerta
                    time      : 3,      // Tiempo para el cierre automático de la alerta
                },

            // Si se ha copiado el enlace a la url del track en el portapapeles
            linkCopied: false,

        },
    mounted: function () {

        // Fija el idioma actual de la página
        this.lang = lang.value;

        // Carga la información del usuario
        this.loadUser();

        // Carga la información de la montaña
        this.loadMountain();

        // Carga los proveedores de cartografía digital
        this.loadMapProviders();

        // Carga la información del track
        this.loadTrackInfo();

        // Inicializa el mapa
        this.initMap();

        // Carga las imágenes
        this.loadImages();

        // Carga los comentarios o posts
        this.loadPosts();
    },
    methods: {
        /**
         * Carga la información del usuario
         *
         */
        loadUser: function () {

            // Carga el usuario
            this.user = JSON.parse(user.value);
        },
        /**
         * Carga la información de la montaña
         *
         */
        loadMountain: function () {
            this.mountain = JSON.parse(mount.value);
            // Añade la posición de la cumbre
            this.mountain.position =
                {
                    utm :
                        {
                            zone    : this.mountain.utmZone,
                            x       : this.mountain.utmX,
                            y       : this.mountain.utmY,
                        },
                    geo :
                        {
                            latitude : this.mountain.latitude,
                            longitude: this.mountain.longitude,
                        },
                };
        },
        /**
         * Carga las imágenes
         *
         */
        loadImages: function () {
            this.images = JSON.parse(images.value);

            this.images.forEach(image => this.completeImage(image));
        },
        /**
         * Completa la información de una imagen
         *
         * @param {Image} image
         *
         */
        completeImage: function (image) {
            // Añade la ruta de la imagen
            image.src     = `/storage/tracks/images/${image.image}`;
            // Añade la ruta de la imagen en miniatura
            image.thumb   = `/storage/tracks/images/thumbs/${image.image}`;
            // Si no hay fecha en la que ha sido obtenida la imagen, se usa la fecha de creación
            if (!image.taken_at) {
                image.taken_at = image.created_at;
            }

            // La descripción (pié de la imagen) incluye el autor, el título y la fecha en la que ha sido obtenida o,
            // en su defecto, la fecha de creación
            image.description = `<a href="${image.fullUrl}">
                                    <i class="fas fa-camera fa-2x"></i></a>
                                 <span class="text-align-fa-2x ml-2">
                                    ${this.track.author} :: ${moment(image.taken_at ? image.taken_at : image.created_at).format('DD-MM-YYYY')}
                                 </span>`;
        },
        /**
         * Carga el track
         *
         */
        loadTrackInfo: function () {
            // Carga el track
            this.track  = JSON.parse(track.value);
            // Carga la autoría del track
            this.author = JSON.parse(author.value);
        },
        /**
         * Carga los comentarios
         *
         */
        loadPosts: function () {
            this.posts = JSON.parse(posts.value);
        },
        /**
         * Carga la información de la montaña
         *
         */
        loadMapProviders: function () {
            this.providers = JSON.parse(providers.value);
        },
        /**
         * Inicializa el mapa
         *
         */
        initMap: function () {

            // Obtiene el proveedor de cartografía a utilizar por defecto
            const defaultProvider = this.$refs.providers.dataset.default;

            this.map =
                {
                    object      : this.$refs.map.mapObject,
                    zoom        : parseInt(map.dataset.zoomStart),
                    center      : latLng(this.mountain.latitude, this.mountain.longitude),
                    provider    : this.providers[defaultProvider],
                    summits     : [],
                };

            this.drawTrack();
        },
        /**
         * Dibuja el trazado del track
         *
         */
        drawTrack: function () {

            const $this = this;

            // Dibuja el gráfico de eleveaciones
            const elevation = L.control.elevation();
            elevation.addTo($this.map.object);

            // Dibuja el track
            // @link https://github.com/mpetazzoni/leaflet-gpx
            new L.GPX($this.track.path, {
                async: true,
                polyline_options:
                    {
                        color: '#2487ce',
                        opacity: 0.75,
                        weight: 5,
                        lineCap: 'round'
                    },
                marker_options: {}
            })
            .on('addline',function(e){
                elevation.addData(e.line);
            })
            .on('loaded', function(e) {
                $this.map.object.fitBounds(e.target.getBounds());
            }).addTo($this.map.object);
        },
        /**
         * Obtiene los límites del mapa
         *
         * @returns {Bounds}        Los límites del mapa
         *
         */
        getMapBounds: function () {
            let bounds = this.map.object.getBounds();

            return {
                north : bounds.getNorth(),
                east  : bounds.getEast(),
                south : bounds.getSouth(),
                west  : bounds.getWest(),
            };
        },
        /**
         * Actualiza las cumbres en el mapa
         *
         */
        updateSummits: function () {

            // Actualiza el nivel de zoom del mapa
            this.map.zoom = this.map.object.getZoom();

            // No se admite un nivel de zoom por encima del máximo para el proveedor de cartografñia digital
            if (this.map.zoom > this.map.provider.options.maxZoom) {
                this.map.object.setZoom(this.map.provider.options.maxZoom);
                return;
            }

            // Obtiene los límites del mapa
            let bounds = this.getMapBounds();

            Axios.post('/com/map/get-mountains',
                {
                    bounds: bounds,
                }
            ).then(response => {
                this.map.summits = response.data;
            }).catch(error => {
                console.error(error);
            });
        },
        /**
         * Al hacer click en el mapa se activa el uso de la rueda del ratón
         * para hacer zoom sobre el mismo
         *
         *
         */
        toggleScrollWheelZoom: function () {

            if (this.map.object.scrollWheelZoom.enabled()) {
                this.map.object.scrollWheelZoom.disable();
            } else {
                this.map.object.scrollWheelZoom.enable();
            }
        },
        /**
         * Cambia el proveedor de cartografía digital
         *
         * @param {Event} event
         *
         */
        changeMapProvider: function (event) {
            // Obtiene el provvedor de cargografía seleccionado
            const selectedProvider = event.target.value;

            // Selecciona el proveedor de cartografía
            for (const provider in this.providers) {
                if (this.providers[provider].name == selectedProvider) {
                    this.map.provider = this.providers[provider];
                }
            }
        },
        /**
         * Abre el visor para mostrar una imagen de la galería
         *
         * @param {Number} image
         */
        showImage(image) {

            // Cambia la imagen
            this.image = image;

            // Se produce un alineamiento no deseado mientras carga la imagen
            // que curiosamente se resuleve al redimensionar la pantalla
            // Por ello, se fuerza el evento 'resize' con una demora que parece igualmente necearia
            setTimeout(()=> {
                window.dispatchEvent(new Event('resize'));
            }, 100)
        },
        /**
         * Confirmar la eliminación de la imagen
         *
         * @param {Image} image     La imagen a eliminar
         *
         */
        confirmDeleteImage: function (image) {

            // Selecciona la imagen para ser eliminada
            image.deleting = true;

            // Abre la modal para confirmar la eliminación de la imagen
            this.$bvModal.show('modal-confirm-delete-image');

        },
        /**
        * Si el usuario actual puede editar o eliminar la imagen
        *
        * @param {Image} image     Una imagen
        *
        * @returns {Boolean}
        *
        */
        userCanEditOrDeleteImage: function (image) {
           // Si el usuario es administrador o es el autor de la imagen
           return this.userIsAdmin || (this.user != null && image != null && this.user.id == this.track.user_id);
        },
        /**
         * Edita la siguiente imagen
         *
         */
        nextImage: function () {

            // Anula el contador de la alerta de guardado si estuviese activado
            this.dismiss.countDown = 0;

            // Obtiene el índice de la siguiente imagen
            let index = this.images.indexOf(this.currentImage);
            ++index;
            if (index ==  this.images.length) index = 0;

            this.currentImage = this.images[index];
        },
        /**
         * Edita la imagen previa
         *
         */
        previousImage: function () {

            // Anula el contador de la alerta de guardado si estuviese activado
            this.dismiss.countDown = 0;

            // Obtiene el índice de la siguiente imagen
            let index = this.images.indexOf(this.currentImage);
            --index;
            if (index == -1) index = this.images.length - 1;

            this.currentImage = this.images[index];
        },
        /**
         * Actualiza el contador para que una alerta despararezca
         *
         * @param {Number} dismissCountDown     El contador en segundos
         *
         */
        countDownChanged(dismissCountDown) {
            this.dismiss.countDown = dismissCountDown;
        },
        /**
         * Elimina la imagen seleccionada
         *
         */
        deleteImage: function () {

            // Obtiene la imagen a eliminar
            const image = this.images.filter(image => image.deleting)[0];

            // Elimina la imagen del servidor
            Axios.post(`/com/track/remove/image/${image.id}`)
                .then(response => {

                     // Elimina la imagen de la lista de archivos subidos
                    this.files = this.files.filter(file => file.image != image);

                    // Elimina la imagen de la lista
                    this.images = this.images.filter(_image => _image != image);

                    // Oculta la modal para confirmar la eliminación de la imagen
                    this.$bvModal.hide('modal-confirm-delete-image');

                })
                .catch(error => {
                    console.error(error);
                });
        },
        /**
         * Elimina de forma inmediata una imagen recién subida
         *
         * @param {Image} image     La imagen
         *
         */
        deleteUploadedImage: function (image) {
            // Marca la imagen para su eliminación
            image.deleting = true;

            // Elimina la imagen
            this.deleteImage();
        },
        /**
         * Cancela la eliminación de una imagen
         *
         */
        cancelDeleteImage: function () {
            // Desmarca como eliminable cualquier imagen
            this.images.forEach(image => image.deleting = false);

            // Cierra la modal
            this.$bvModal.hide('modal-confirm-delete-image');
        },
        /**
         * Edita el track
         *
         */
        editTrack: function () {

            // Fija el modo edición
            this.setMode('edit');

            // Mueve la posición hasta la descripción para su edición
            this.$refs.description.scrollIntoView();

        },
        /**
         * Guarda el track
         *
         */
        saveTrack: function () {

            // Obtiene el texto (desde el editor tinymce)
            this.track.text = tinymce.get('text').getContent();

            waiting.show();

            // Guarda la información del track
            Axios.post(`/com/track/save/${this.track.id}`,
                this.track
            ).then(() => {

                    // Pasa al modo visor
                    this.setMode('view');

                    // Oculta el loader
                    waiting.hide();

                    // Muestra la modal indicando que la información del track se ha guardado con éxito
                    this.$bvModal.show('modal-save-success');
            })
            .catch(error => {

                console.error(error);

                // Oculta el loader
                waiting.hide();

                // Muestra la modal indicando que la información proporcionada no es correcta
                this.$bvModal.show('modal-save-failed');

            });
        },
        /**
         * Confirma la eliminación del track
         *
         */
        confirmDeleteTrack: function () {
            // Abre la modal de confirmación de eliminación
            this.$bvModal.show('modal-confirm-delete-track');
        },
        /**
         * Elimina el track
         *
         */
        deleteTrack: function () {
            Axios.post(`/com/track/delete/${this.track.id}`)
                .then(() => {
                    // Oculta la modal de confirmación de la eliminación
                    // y muestra la modal que indica que la montaña ha sido eliminada
                    this.$bvModal.hide('modal-confirm-delete-track');
                    this.$bvModal.show('modal-delete-track-confirmed');
                })
                .catch(error => {
                    console.error(error);
                });
        },
        /**
         * Modifica la autoría del track
         *
         */
        changeAuthor: function () {
            // Abre la modal de selección de usuarios para seleccionar un nuevo autor para el track
            this.userModalOpen(this.selectUserAsTrackAuthor);
        },
        /**
         * Al seleccionar un archivo de imagen
         *
         * @param {File} newFile      El archivo nuevo          [Lectura y Escritura]
         * @param {File} oldFile      El archivo antiguo        [Sólo Lectura]
         *
         */
        inputImage: function (newFile, oldFile) {

            if (!newFile) return;

            // Subida automática de los archivos seleccionados
            // Cuando el archivo es cargado por vez primera (al inicio de la subida)
            if (Boolean(newFile) !== Boolean(oldFile) || oldFile.error !== newFile.error) {
                // Si la subida no está aún activa
                if (!this.$refs.upload.active) {

                // Si el tamaño del archivo excede el tamaño admisible
                    if (newFile.file.size > this.upload.maxFileSize) {
                        newFile.error = 'abort';
                        return false;
                    }

                    // Marca la subida como activa
                    this.$refs.upload.active = true;
                }
            }
        },
        /**
         * Al filtrar los archivos de imágenes
         *
         * @param {File}     newFile   El archivo nuevo         [Lectura y Escritura]
         * @param {File}     oldFile   El archivo viejo         [Sólo Lectura]
         * @param {Function} prevent   Función que evita que la lista de archivos subidos sea cambiada
         *
         */
        inputFilterImage: async function (newFile, oldFile, prevent) {

            if (!newFile) return;

            // Si es un archivo nuevo y no uno viejo
            if (!oldFile) {
                // Comprueba que el archivo es una imagen válida
                if (!this.upload.imagesAcceptedTypes.includes(newFile.type)) {
                    // Muestra el mensaje de error, que el archivo no es válido para ser subido
                    this.$bvModal.show('file-image-not-valid');
                    // Evitar la actualización en la lista de archivos
                    return prevent();
                }
            }

            // El archivo ha sido subido con éxito
            if (newFile.success) {

                // Obtiene la imagen subida y completa su información
                let image = newFile.response;
                this.completeImage(image);

                // Añade la imagen al archivo subido
                // lo que permite poder eliminar el archivo o editarlo desde la tabla de archivos subidos
                newFile.image = image;

                // Para, finalmente, añadirla a la lista de imágenes
                this.images.push(image);
            }

            // Crea un objeto Blob
            newFile.blob = URL.createObjectURL(newFile.file);

            // Obtiene una miniatura de la imagen
            newFile.thumb = await this.resizeImage(newFile.blob, 100, 100);

        },
        /**
         * Redimensiona una imagen
         *
         * @param {String} data             Los datos de la imagen en formato Base64
         * @param {Number} wantedWidth      El ancho de la imagen requerido en píxeles
         * @param {Number} wantedHeight     El alto de la imagen requerido en píxeles
         *                                  Si no se especifica, se calcula un alto proporcional
         *                                  al ancho de la imagen original
         *
         * @return {Promise}                Una promesa con la imagen redimensionada en Base64
         *
         */
        resizeImage: function (data, wantedWidth, wantedHeight) {

            return new Promise(resolve => {

                // Creamos una nueva Imagen
                let image = new Image();

                // y cargamos a la imagen a redimensionar
                image.src = data;

                // Cuando la imagen está cargada
                image.onload = () =>
                    {
                        // Si no se ha proporcionado una altura, la obtiene en relación a la anchura
                        if (typeof wantedHeight === 'undefined') {
                            wantedHeight = wantedWidth * image.height / image.width
                        }

                        // Creamos un nuevo elemento canvas para efectuar la operación
                        let canvas = document.createElement('canvas');
                        let ctx    = canvas.getContext('2d');

                        // y lo dimensionamos con las dimensiones requeridas
                        canvas.width  = wantedWidth;
                        canvas.height = wantedHeight;

                        // Dibujamos la imagen
                        ctx.drawImage(image, 0, 0, wantedWidth, wantedHeight);

                        // Devolvemos la imagen redimensionada
                        resolve(canvas.toDataURL());
                    };
            });
        },
        /**
         * Al mover una imagen
         *
         * @param {Event} event
         *
         */
        onEndMoveImage: function (event) {
            // Obtiene el índice del elemento movido (from)
            // El primer es elemento es el que posee índice nulo
            const from = event.oldIndex;
            // Obtiene el nuevo índice (lugar) del elemento que ha sido movido (to)
            const to   = event.newIndex;

            // La imagen que ha sido movida
            const image = this.images[from];

            // Construye la lista de ids de las imágenes tras el movimiento
            const images = this.images.map(image => image.id)

            //
            // Bloqueamos la interfaz mientras se produce el ordenamiento de imágenes
            // actuando sobre la galería de imágenes
            //
            document.querySelector('.image-gallery').style.pointerEvents = 'none';

            // Envía la lista de imágenes según su posición actual
            Axios.post(`/com/track/move/image/${image.id}`,
                {
                    images: images    // La lista de imágenes tras la ordenación
                }
            )
            .then(() => {
                const move = response.data;
                console.info(`La imagen se ha movido con éxito de la posición ${from} a la posición ${to}`);
                // Recupera el control sobre la galería de imágenes
                document.querySelector('.image-gallery').style.pointerEvents = 'auto';
            })
            .catch(error => {
                console.warn(`Operación no permitida para el usuario actual`);
                // Recupera el control sobre la galería de imágenes
                document.querySelector('.image-gallery').style.pointerEvents = 'auto';
            });
        },
        /**
         * Guarda la imagen
         *
         * @param {Image} Image     Una imagen
         *
         */
        saveImage: function (image) {
            Axios.post(`/com/track/edit/image/${image.id}`,
                {
                    user_id     : image.user_id,
                    taken_at    : image.taken_at,
                    title       : image.title
                }
            )
            .then(() => {

                // Actualiza la información de la imagen
                this.completeImage(image);

                // Tiempo para cerrar la alerta que muestra que los
                // datos de la imagen se han guardado con éxito
                this.dismiss.countDown = this.dismiss.time;
            })
            .catch(error => {
                console.error(error);
            });
        },
        /**
         * Busca un usuario
         *
         */
        searchUser: function () {
            Axios.post(`/com/user/search`,
                {
                    search : this.searched.user,        // El nombre, apellidos, email del usuario a buscar
                }
            )
            .then(response => {
                this.searched.users = response.data;
            })
            .catch(error => {
                console.error(error);
            });
        },
        /**
         * Abre la modal de selección de usuario
         *
         * @param {Callback} userSelect     El método a ejecutar cuando se ha seleccionado el usuario
         *                                  Una vez se ha procedido a seleccionar un usuario, se ejecuta el método indicado
         *
         */
        userModalOpen: function (userSelect) {

            // Fija la función de callback cuando se ha seleccionado el usuario
            this.userSelect = userSelect;

            // Abre la modal de selección del usuario
            this.$bvModal.show('modal-select-user');

        },
        /**
         * Cuando se ha seleccionado un usuario en la modal de selección de usuarios
         * Se cierra la modal y se eejcuta el método de callback
         *
         * @param {User} user       El usuario
         */
        userModalSelect: function (user) {

            // Cierra la modal de selección del usuario
            this.$bvModal.hide('modal-select-user');

            // Ejecuta la función de callback que contiene al usuario seleccionado
            this.userSelect(user);
        },
        /**
         * Selecciona un usuario como autor del track
         *
         * @param {User} user       Un usuario
         *
         */
        selectUserAsTrackAuthor: function (user) {
            // Añade el usuario
            this.track.user_id = user.id;
            this.track.author  = `${user.name} ${user.lastname}`;
            this.track.author_image  = user.thumb;
        },
        /**
         * Fija el modo de visualización de la información
         *
         * @param {String} mode     El modo: 'view'|'edit' (visor|editor)
         *
         */
        setMode: function (mode) {
            this.mode = mode;



            // Habilita/Deshabilita el editor tinymce en el campo de edición del texto del track
            if (this.mode == 'edit') {
                // Elimina los anuncios de Adsense
                document.querySelectorAll('.adsbygoogle')
                        .forEach(ad => ad.remove());

                tinymce.init({
                    selector    : '#text',
                    language    : this.lang,
                    language_url: `/assets/vendor/tinymce/langs/${this.lang}.js`,
                    plugins     : 'link autolink image lists table',
                    contextmenu : 'copy cut paste | link image',
                    toolbar     : 'undo redo | removeformat | styleselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image table',
                    statusbar   : false,
                    inline      : true,
                    menubar     : false,
                });
            } else {
                tinymce.remove();
            }
        },
        /**
         * Abre el visor para mostrar una imagen de la galería
         *
         * @param {Number} image    El índice de la imagen
         *
         */
        showImage(image) {

            // Cambia la imagen
            this.image = image;

            // Se produce un alineamiento no deseado mientras carga la imagen
            // que curiosamente se resuleve al redimensionar la pantalla
            // Por ello, se fuerza el evento 'resize' con una demora que parece igualmente necearia
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 250)
        },
        /**
         * Elimina la imagen
         *
         * @param {Image} image     La imagen a eliminar
         *
         */
        removeImage(image) {

            // Elimina la imagen del servidor
            Axios.post(`/com/track/remove/image/${image.id}`)
                .then(() => {

                    // Elimina la imagen de la lista de archivos subidos
                    this.files = this.files.filter(file => file.image != image);

                    // Elimina la imagen de la lista
                    this.images = this.images.filter(_image => _image != image);
                })
                .catch(error => {
                    console.error(error);
                });
        },
        /**
         * Edita la imagen
         *
         * @param {Number} image
         *
         */
        editImage(image) {

            // La imagen actulamente seleccionada para la edición de su información
            this.currentImage = image;

            // Abrir la modal para cambiar los datos de la imagen
            this.$bvModal.show('modal-edit-image');
        },
        /**
         * Descarga el archivo de track
         *
         */
        downloadTrackFile: function () {
            // Oculta la modal
            this.$bvModal.hide('modal-before-track-download');

            // Descarga el archivo en formato zip en equipos de escritorio y gpx en móviles
            if (isMobile) {
                location.href = `/com/track/download/gpx/${this.track.md5}`;
            } else {
                location.href = `/com/track/download/zip/${this.track.md5}`;
            }
        },
        /**
         * Descarga el archivo de Google Track
         *
         */
        downloadGoogleEarthFile: function () {
            // Oculta la modal
            this.$bvModal.hide('modal-before-google-earth-download');
            // Descarga el archivo en formato kmz
            location.href = `/com/track/download/kmz/${this.track.md5}`;
        },
        /**
         * Si el usuario actual puede editar o eliminar un comentario
         *
         * @param {Post} post       Un mensaje o comentario
         *
         * @returns {Boolean}
         *
         */
        userCanEditOrDeletePost: function (post) {
            // Si el usuario es administrador o es el autor del comentario o post
            return this.userIsAdmin || (this.user && post && this.user.id == post.user_id);
        },
        /**
         * Obtiene las iniciales del usuario autor de un comentario
         *
         * @param {String} author   El autor
         *
         * @returns {String}        Las iniciales del usuario
         *
         */
        getAuthorInitials: function (author) {
            let regex= new RegExp(/(\p{L}{1})\p{L}+/, 'gu');

            let initials = [...author.matchAll(regex)] || [];

            return (
              (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
            ).toUpperCase();
        },
        /**
         * Crea un nuevo mensaje o post
         *
         */
        createPost: function () {

            // Crea el post
            const post =
                {
                    id          : 0,
                    user_id     : this.user.id,
                    created_at  : new Date(),
                    author      : `${this.user.name} ${this.user.lastname}`,
                    message     : null,
                    image       : this.user.thumb,
                    editable    : true,
                };

            this.posts.unshift(post);

            // Re-renderizar la instancia
            vm.$forceUpdate();

            // Edita el nuevo mensaje
            setTimeout(()=> {this.editPost(post);}, 1000);
        },
        /**
         * Guarda el comentario o post
         *
         * @param {Post} post
         *
         */
        savePost: function (post) {

            // Guarda el contenido del post
            post.message = tinymce.get(`post${post.id}`).getContent();

            // Si no hay mensaje no se permite guardar
            if (!post.message) {
                tinymce.get(`post${post.id}`).focus();
                return;
            }

            // Elimina el editor
            tinymce.get(`post${post.id}`).remove();

            post.editing = false;

            if (post.id) {
                // Edición de un post ya existente
                Axios.post(`/com/track/post/edit`, {
                    post : post,
                })
                .then(() => {
                    // Comentario guardado con éxito
                    this.$bvModal.show('modal-post-saved-success');
                })
                .catch(error => {
                    console.error(error);
                });
            } else {
                // Creación de un nuevo post
                Axios.post(`/com/track/post/create/${this.track.id}`, {
                    post : post,
                })
                .then(response => {
                    // Fija el id del nuevo comentario o post creado
                    post.id = response.data.id;
                    // Muestra la modal que se ha guardado el post con éxito
                    this.$bvModal.show('modal-post-saved-success');
                })
                .catch(error => {
                    console.error(error);
                });
            }

            // Re-renderizar la instancia
            vm.$forceUpdate();
        },
        /**
         * Cancela la edición del post sin guardar
         *
         * @param {Post} post
         *
         */
        cancelPost: function (post) {

            // Elimina el editor
            tinymce.get(`post${post.id}`).remove();

            // Marca el comentario como no editado
            post.editing = false;

            // Re-renderizar la instancia
            vm.$forceUpdate();
        },
        /**
         * Confirma la eliminación de un comentario o post
         *
         * @param {Post} post
         *
         */
        confirmDeletePost: function (post) {

            // Marca el post como eliminable
            post.deleting = true;

            // Abrir la modal para confirmar la eliminación del comentario
            this.$bvModal.show('modal-confirm-delete-post');
        },
        /**
         * Cancela la eliminación de un comentario
         *
         */
        cancelDeletePost: function () {
            // Desmarca como eliminable cualquier comentario
            this.posts.forEach(post => post.deleting = false);

            // Cierra la modal
            this.$bvModal.hide('modal-confirm-delete-post');
        },
        /**
         * Elimina el comentario o post
         *
         */
        deletePost: function () {

            // Obtiene el post a eliminar
            const post = this.posts.filter(post => post.deleting)[0];

            // Eliminación de un post
            Axios.post(`/com/track/post/delete`, {
                post : post,
            })
            .then(() => {
                // Elimina el post del conjunto de posts
                this.posts = this.posts.filter(_post => _post != post);
                // Cierra la modal
                this.$bvModal.hide('modal-confirm-delete-post');
            })
            .catch(error => {
                console.error(error);
            });
        },
        /**
         * Edita el comentario o post
         *
         * @param {Post} post
         *
         */
        editPost: function (post) {
            tinymce.init({
                selector        : `#post${post.id}`,
                language        : this.lang,
                language_url    : `/assets/vendor/tinymce/langs/${this.lang}.js`,
                browser_spellcheck: true,
                plugins         : 'link autolink image lists table code',
                toolbar         : 'undo redo | removeformat | styleselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image table code',
                toolbar_mode    : 'scrolling',
                statusbar       : false,
                inline          : true,
                menubar         : false,
                paste_as_text   : true,
                paste_text_sticky : true,
                toolbar_sticky  : true,
                init_instance_callback: function (editor) {
                    // Fija el foco al iniciar y se muestra la barra de herramientas
                    editor.fire('focus');
                },
                setup: function (editor) {
                    // Previene que el evento blur oculte la barra de herramientas
                    editor.on('blur', function () {
                        return false;
                    });
                },
            });

            post.editing = true;

            // Re-renderizar la instancia
            vm.$forceUpdate();
        },
        /**
         * Comprueba si el post está siendo editado o no
         *
         * @param {Post} post
         *
         */
        postIsEditing: function  (post) {
            return post.editing;
        },
        /**
         * Carga un track para ser subido
         *
         */
        loadTrack: function () {
            // Abre el explorador de archivos para seleccionar un track
            trackFile.click();
        },
        /**
         * Sube el archivo de track
         *
         * @param {Event} event
         *
         */
        uploadTrack: function(event) {

            let formData = new FormData();

            formData.append('track', event.target.files[0]);

            Axios.post(`/com/track/reupload/${this.track.id}`,
                formData,
                {
                    headers:
                        {
                            'Content-Type': 'multipart/form-data',
                        },
                        onUploadProgress: function(progressEvent) {
                            this.upload.progress = parseInt( Math.round( ( progressEvent.loaded / progressEvent.total ) * 100 ));
                        }.bind(this)
                },
            )
            .then(response => {
                // Obtiene la información del track
                let track = response.data;

                if (typeof track.id === 'undefined') {
                    // Si la respuesta no devuelve un track
                    this.errors.track.message = null;

                    // Vuelve la barra de progreso de subida de archivo a cero
                    this.upload.progress = 0;

                    // Abre la modal mostrando el error
                    this.$bvModal.show('file-track-not-valid');
                } else {
                    // Redirige a la página del track
                    location.href = `/com/track/${track.id}`;
                }
            })
            .catch(error => {

                // Obtiene el mensaje de error si existe
                if (error.response) {
                    this.errors.track.message = error.response.data.message;
                }

                // Vuelve la barra de progreso de subida de archivo a cero
                this.upload.progress = 0;

                // Abre la modal mostrando el error
                this.$bvModal.show('file-track-not-valid');
            });
        },
        /**
         * Muestra la información del autor
         *
         * @param {Author} author
         *
         */
        showAuthorInfo: function (author) {

            // Muestra el loader
            waiting.show();

            // Obtiene la información del autor
            Axios.post(`/com/user/author/info/${author.id}`)
                .then(response => {

                    // Oculta el loader
                    waiting.hide();

                    // La información del autor
                    this.authorInfo = response.data;

                    // Oculta la modal de confirmación de la eliminación
                    this.$bvModal.show('modal-author-info');
                })
                .catch(error => {
                    console.error(error);
                });
        },
        /**
         * Comparte la página actual de información del track
         *
         * @param {Track} track
         */
        shareTrack: async function (track) {

            try {
                await navigator.share(
                    {
                        title: track.description,
                        text: track.description,
                        url: track.url
                    }
                );
                console.info(`Se ha compartido la url del track ${track.url}`);
            } catch (error) {
                // Copia la url en el portapapeles
                navigator.clipboard.writeText(track.url)
                    .then(() => {
                        this.linkCopied = true;
                        console.info(`Se ha copiado la url del track ${track.url} en el portapapeles`);
                    })
                    .catch(error => {
                        console.error(error);
                    });
            }

        },
    },
    computed: {
        /**
         * Si el agente de usuario actual es un dispositivo móvil o no
         *
         * @returns {Boolean}
         *
         */
        isMobile: function() {
            return isMobile;
        },
        /**
         * Si la información es editable o no
         *
         * @returns {Boolean}
         *
         */
        editable: function () {
            return this.mode == 'edit';
        },
        /**
         * Si la información no es editable o si
         *
         * @returns {Boolean}
         *
         */
        notEditable: function () {
            return this.mode == 'view';
        },
        /**
         * Cuando el mapa ha sido inicializado
         *
         * @returns {Boolean}       true si el mapa ha sido inicializado
         */
        mapInitialized: function () {
            return this.map.provider != null;
        },
        /**
         * Si el usuario actual es administrador o no
         *
         * @returns {Boolean}
         *
         */
        userIsAdmin: function () {
            return this.user && (this.user.privileges == 1 || this.user.privileges == 2);
        },
        /**
         * Comprueba si un nuevo comentario o post ya ha sido creado
         *
         * @returns {Boolean}
         *
         */
         newPostAlreadyCreated: function () {
            // Si se ha creado ya un comentario, no se permite crear otro nuevo
            // El post ya creado tiene id nulo
            return this.posts.filter(post => post.id == 0).length != 0;
        },
    },
    filters: {
        /**
         * Formatea el nombre de una cumbre
         *
         * @param {String} name         El nombre de la cumbre
         *
         */
        name: function (name) {
            return name.split('/')[0];
        },
        /**
         * Formatea un valor de altitud
         *
         * @param {Number} value        Un valor de altitud
         * @returns {String}            La altitud formateada
         *
         */
        altitude: function (value) {
            return `${parseInt(value)} m`;
        },
         /**
         * Formatea un valor de distancia
         *
         * @param {Number} value        Un valor de distancia
         * @returns {String}            La distancia formateada
         *
         */
        distance: function (value) {
            return `${numeral(value / 1000).format('0.000').replace('.', ',')} km`;
        },
        /**
         * Formatea una fecha
         *
         * @param {Date} value          Una fecha
         * @param [String} type         Representación de la fecha en formato largo o corto
         *                                  'short'|'long'
         * @returns {String}            Una fecha formateada
         */
        date: function (value, type = 'long') {
            if (type == 'long') {
                return lang.value == 'eu' ?
                    moment(value).locale(lang.value).format('YYYY[-(e)ko] MMMM[k] D')
                                        :
                    moment(value).locale(lang.value).format('D MMMM YYYY');
            } else {
                return lang.value == 'eu' ?
                    moment(value).format('YYYY-MM-DD')
                                    :
                    moment(value).format('DD-MM-YYYY');
            }
        },
        /**
         * Formatea un tiempo en segundos
         *
         * @param {Number} value        Tiempo en segunos
         * @returns {String}            El tiempo formeado
         */
        timeFromSeconds: function (value) {
            return moment().startOf('day')                          // Con respexto al inicio del día actual
                           .seconds(Math.round(value / 60) * 60)    // Redondeo
                           .format('H:mm');                         // Formato H:mm
        },
        /**
         * Formatea un tamaño de archivo
         *
         * @param {Number} value        El tamaño de archivo en bytes
         *
         * @returns {String}            El tamaño de archivo formateado
         *
         */
        filesize: function (value) {
            return value < 1024 * 1024 ? `${numeral(value/1024).format('0')} kB` : `${numeral(value/1024/1024).format('0.0').replace('.', ',')} MB`;
        },
        /**
         * Recorta un texto limitando el número de palabras a mostrar
         *
         * @param {String} value        La cadena de texto
         * @param {Number} nwords       El número de palabras a mostrar
         *                              Por defecto, 3
         *
         * @return {String}             La cadena de texto acortada
         */
        shortenText: function (value, nwords = 3) {

            if (!value) return value;

            // Obtiene las palabras del texto
            const words = value.split(' ');

            // Limita el número de palabras a mostrar
            return words.length > nwords ? words.slice(0, nwords).join(' ') + '...' : value;
        },
    }
 });
